import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import baseApi from '../../../api/baseApi';
import { CATEGORY, ENTITY, ERRORS } from '../../../constants';
import { buildRequest, getDataFromResponse, setErrorStatus, getStatisticsUrlForEntry, getStatisticsImg, getEntryPageTitle, getStatisticsUrlForPredictiveSearch } from '../../../utils';

import useFetch from '../../Hooks/useFetch';

import Content from './Content/Content';
import DecisionTree from './DecisionTree/DecisionTree';
import Breadcrumb from '../../UI/Breadcrumb/Breadcrumb';
import FeedbackBox from '../../UI/FeedbackBox/FeedbackBox';
import ListLinksSection from '../../UI/ListLinksSection/ListLinksSection';
import Loader from '../../UI/Loader/Loader';
import PageNotFound from '../Error/PageNotFound/PageNotFound';
import PageUnavailable from '../Error/PageUnavailable/PageUnavailable';
import Aside from '../../UI/Aside/Aside';

const defaultEntryState = {
    name: '',
    ref: '',
    uid: '',
    itemId: '',
    description: '',
    content: null,
    decisionTree: null,
    fetchTimestamp: null
};

const defaultLastStepState = {
    choices: null,
    uid: null,
    ref: null
};

const Entry = () => {
    const { t } = useTranslation();
    const [isMounted, setIsMounted] = useState(false);
    const { category, entry } = useParams();
    const isAlertCategory = useCallback(() => category === CATEGORY.alerts, [category]);
    const location = useLocation();
    const [statisticsUrl, setStatisticsUrl] = useState('');
    const [predictiveSearchStatisticsUrl, setPredictiveSearchStatisticsUrl] = useState('');
    const [state, setState] = useState(defaultEntryState);
    const [categoryId, setCategoryId] = useState(category);
    const [categoryName, setCategoryName] = useState('');
    const [error, setError] = useState(null);
    const [lastStep, setLastStep] = useState(defaultLastStepState);

    const [alerts, isFetchingAlerts, alertsError] = useFetch(
        buildRequest(ENTITY.alerts, [CATEGORY.alerts]),
        category);
    const [categoryDetails] = useFetch(
        buildRequest(ENTITY.categoryDetails, [category]),
        category);
    const [entries, isFetchingEntries, entriesError] = useFetch(
        !isAlertCategory() && buildRequest(ENTITY.entriesByCategoryId, [categoryId]),
        !isAlertCategory() && category);
    const [relatedContent, isFetchingRelatedContent, relatedContentError] = useFetch(
        !isAlertCategory() && state.itemId && buildRequest(ENTITY.relatedContent, [category, state.itemId]),
        !isAlertCategory() && entry);

    useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);

    useEffect(() => {
        if (!isMounted) {
            return;
        }

        const url = isAlertCategory()
            ? `${category}/${entry}`
            : `/categories/${category}/entries/${entry}`;

        setState(defaultEntryState);
        baseApi
            .get(url)
            .then(getDataFromResponse)
            .then(data => {
                if (!isMounted) {
                    return;
                }

                setState({
                    ...data,
                    fetchTimestamp: Date.now()
                });
            })
            .catch(error => setErrorStatus(setError, error));

        setCategoryId(category);
    }, [category, entry, isAlertCategory, isMounted]);

    useEffect(() => {
        if (!categoryDetails.name) {
            return;
        }
        setCategoryName(categoryDetails.name);

        const handleStatistics = (uid) => {
            if (!uid) {
                return;
            }
            setStatisticsUrl(getStatisticsUrlForEntry(uid));
        };
        handleStatistics(state.uid);
    }, [categoryDetails, setCategoryName, state.uid]);

    useEffect(() => {
        if (!location?.state?.searchPhrase) {
            return;
        }

        const statisticsImgUrl = getStatisticsUrlForPredictiveSearch(location.state.searchPhrase, location.state.searchConfidence, entry);
        if (!statisticsImgUrl) {
            return;
        }

        const removeLocationState = () => window.history.replaceState({}, document.title);
        removeLocationState();
        setPredictiveSearchStatisticsUrl(statisticsImgUrl);
    }, [entry, location?.state?.searchPhrase, location?.state?.searchConfidence, setPredictiveSearchStatisticsUrl]);

    if (!isMounted) {
        return null;
    }

    if (error === ERRORS.notFound) {
        return <PageNotFound />;
    }

    if (error) {
        return <PageUnavailable />;
    }

    const decisionTreeComponent = state.decisionTree && (
        <DecisionTree
            key={state.fetchTimestamp}
            decisionTree={state.decisionTree}
            setLastStep={setLastStep}
        />
    );

    const contentComponent = state.content && (
        <Content key={state.fetchTimestamp} content={state.content} />
    );

    const body = decisionTreeComponent
        ? decisionTreeComponent
        : contentComponent;

    const collateralContent = () => {
        if (isAlertCategory() || !body) {
            return null;
        }

        const shouldDisplayCollateralContent = contentComponent || (lastStep.uid && !lastStep?.choices?.length);
        if (!shouldDisplayCollateralContent) {
            return null;
        }

        const votingUid = decisionTreeComponent ? lastStep.uid : state.uid;
        const feedbackEntryRef = decisionTreeComponent ? lastStep.ref : state.ref;

        return (
            <>
                <FeedbackBox
                    key={entry}
                    entryId={entry}
                    entryRef={feedbackEntryRef}
                    entryUid={votingUid}
                />
                {isFetchingRelatedContent
                    ? <div className="help-page__loader help-page__loader--small"><Loader spinner /></div>
                    : !relatedContentError && relatedContent?.length > 0 && (
                        <div data-cy="related-entries-section-wrapper">
                            <ListLinksSection
                                isFetching={isFetchingRelatedContent}
                                heading={t('layout.entry.related.header')}
                                links={relatedContent}
                            />
                        </div>
                    )}
            </>
        );
    };

    const isNotCurrent = x => x.id !== entry;
    const entriesWithoutCurrent = entries.filter(isNotCurrent);
    const alertsWithoutCurrent = isAlertCategory()
        ? alerts.filter(isNotCurrent)
        : alerts;

    const entryName = location?.state?.entryName ?? state.name;

    const helmet = entryName && categoryName && (
        <Helmet>
            <title>{getEntryPageTitle(entryName, categoryName)}</title>
            {state.description && <meta name="description" content={state.description} />}
        </Helmet>
    );

    return (
        <>
            {helmet}
            {predictiveSearchStatisticsUrl && getStatisticsImg(predictiveSearchStatisticsUrl)}
            {statisticsUrl && getStatisticsImg(statisticsUrl)}
            <article className="help-page help-page--subpage">
                <header className="help-page__header">
                    <div className="help-page__wrapper">
                        <h1 className="help-page__heading" data-cy="entry-heading">
                            {!state.fetchTimestamp && !location?.state?.entryName
                                ? <Loader inline dotted />
                                : entryName}
                        </h1>
                        <Breadcrumb
                            entryName={entryName}
                            categoryName={categoryName}
                        />
                    </div>
                </header>

                <div className="help-page__content constrain">
                    <div
                        className="help-page__main-col entry"
                        data-cy="entry-content"
                        data-nr="entry-content"
                    >
                        {!state.fetchTimestamp || !body ? <Loader large spinner /> : body}
                        {collateralContent()}
                    </div>
                    <Aside
                        isFetchingAlerts={isFetchingAlerts}
                        isFetchingContent={isFetchingEntries}
                        alertsError={alertsError}
                        contentError={entriesError}
                        alerts={alertsWithoutCurrent}
                        content={entriesWithoutCurrent}
                        entries={entriesWithoutCurrent}
                        contentHeader={categoryDetails.name}
                        categoryId={categoryDetails.id}
                        isAlertCategory={isAlertCategory()}
                    />
                </div>
            </article>
        </>
    );
};

export default Entry;
